import React from 'react';

/**
 * SVG tomato plant templates with 4 growth stages.
 * Uses compositor-only animations (transform, opacity) for 60fps.
 *
 * Growth stages:
 * 0 = seed (0-25%)
 * 1 = sprout (25-50%)
 * 2 = sapling (50-75%)
 * 3 = full plant (75-100%)
 */

function getGrowthStage(progress) {
  if (progress < 0.25) return 0;
  if (progress < 0.5) return 1;
  if (progress < 0.75) return 2;
  return 3;
}

// ============================================
// Plant Species
// ============================================

const PLANT_SPECIES = {
  roma: {
    name: 'Roma',
    stages: [RomaSeed, RomaSprout, RomaSapling, RomaFull],
    colors: { stem: '#2d5016', leaf: '#4ade80', fruit: '#ef4444' },
  },
  cherry: {
    name: 'Cherry',
    stages: [CherrySeed, CherrySprout, CherrySapling, CherryFull],
    colors: { stem: '#365314', leaf: '#86efac', fruit: '#f87171' },
  },
};

// ============================================
// Roma Tomato Stages
// ============================================

function RomaSeed({ colors }) {
  return (
    <g>
      <ellipse cx="50" cy="88" rx="18" ry="5" fill="#5c3a1e" opacity="0.6" />
      <ellipse cx="50" cy="83" rx="5" ry="7" fill="#c9a96e" />
      <ellipse cx="50" cy="82" rx="3" ry="4" fill="#dbb98a" />
      <line x1="50" y1="78" x2="50" y2="75" stroke={colors.leaf} strokeWidth="1.5" strokeLinecap="round" />
    </g>
  );
}

function RomaSprout({ colors }) {
  return (
    <g>
      <ellipse cx="50" cy="88" rx="16" ry="4" fill="#5c3a1e" opacity="0.5" />
      <path d="M50 88 Q50 70 50 60" stroke={colors.stem} strokeWidth="3" fill="none" strokeLinecap="round" />
      <ellipse cx="43" cy="63" rx="8" ry="4" fill={colors.leaf} transform="rotate(-25 43 63)" />
      <ellipse cx="57" cy="63" rx="8" ry="4" fill={colors.leaf} transform="rotate(25 57 63)" opacity="0.85" />
      <ellipse cx="50" cy="56" rx="5" ry="7" fill={colors.leaf} />
    </g>
  );
}

function RomaSapling({ colors }) {
  return (
    <g>
      <ellipse cx="50" cy="90" rx="20" ry="4" fill="#3d2010" opacity="0.2" />
      {/* Support stake */}
      <line x1="55" y1="90" x2="55" y2="35" stroke="#8B7355" strokeWidth="2" strokeLinecap="round" />
      {/* Main stem */}
      <path d="M50 90 Q49 72 48 45" stroke={colors.stem} strokeWidth="4" fill="none" strokeLinecap="round" />
      {/* Branches */}
      <path d="M48 70 Q40 62 35 58" stroke={colors.stem} strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M49 58 Q58 50 63 46" stroke={colors.stem} strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* Leaves */}
      <ellipse cx="33" cy="55" rx="9" ry="5" fill={colors.leaf} transform="rotate(-15 33 55)" />
      <ellipse cx="64" cy="43" rx="9" ry="5" fill={colors.leaf} transform="rotate(15 64 43)" opacity="0.85" />
      <ellipse cx="44" cy="40" rx="8" ry="5" fill={colors.leaf} transform="rotate(-10 44 40)" />
      {/* Small green tomatoes */}
      <circle cx="38" cy="65" r="4" fill="#86efac" opacity="0.8" />
      <circle cx="60" cy="52" r="3.5" fill="#86efac" opacity="0.7" />
    </g>
  );
}

function RomaFull({ colors }) {
  return (
    <g>
      <ellipse cx="50" cy="92" rx="28" ry="5" fill="#3d2010" opacity="0.25" />
      {/* Support stake */}
      <line x1="56" y1="92" x2="56" y2="20" stroke="#8B7355" strokeWidth="2.5" strokeLinecap="round" />
      {/* Main stem */}
      <path d="M50 92 Q48 70 47 35" stroke={colors.stem} strokeWidth="5" fill="none" strokeLinecap="round" />
      {/* Branches */}
      <path d="M48 75 Q35 65 28 58" stroke={colors.stem} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M48 60 Q60 50 68 44" stroke={colors.stem} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M47 45 Q38 38 32 32" stroke={colors.stem} strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* Leaves */}
      <ellipse cx="26" cy="55" rx="10" ry="5" fill={colors.leaf} transform="rotate(-20 26 55)" />
      <ellipse cx="70" cy="40" rx="10" ry="5" fill={colors.leaf} transform="rotate(20 70 40)" opacity="0.85" />
      <ellipse cx="30" cy="30" rx="9" ry="5" fill={colors.leaf} transform="rotate(-15 30 30)" />
      <ellipse cx="55" cy="25" rx="8" ry="4" fill={colors.leaf} transform="rotate(10 55 25)" opacity="0.85" />
      {/* Ripe red tomatoes */}
      <circle cx="32" cy="62" r="6" fill={colors.fruit} />
      <circle cx="28" cy="68" r="5" fill={colors.fruit} opacity="0.9" />
      <circle cx="65" cy="48" r="5.5" fill={colors.fruit} />
      <circle cx="35" cy="36" r="4.5" fill={colors.fruit} opacity="0.9" />
      <circle cx="60" cy="55" r="4" fill={colors.fruit} opacity="0.85" />
      {/* Tomato highlights */}
      <circle cx="30" cy="60" r="2" fill="white" opacity="0.2" />
      <circle cx="64" cy="46" r="1.5" fill="white" opacity="0.2" />
      {/* Gentle sway animation */}
      <ellipse cx="50" cy="22" rx="7" ry="4" fill={colors.leaf} opacity="0.7">
        <animateTransform attributeName="transform" type="rotate" values="-2 50 22;2 50 22;-2 50 22" dur="4s" repeatCount="indefinite" />
      </ellipse>
    </g>
  );
}

// ============================================
// Cherry Tomato Stages
// ============================================

function CherrySeed({ colors }) {
  return (
    <g>
      <ellipse cx="50" cy="88" rx="18" ry="5" fill="#5c3a1e" opacity="0.6" />
      <ellipse cx="50" cy="83" rx="5" ry="7" fill="#c9a96e" />
      <ellipse cx="50" cy="82" rx="3" ry="4" fill="#dbb98a" />
      <line x1="50" y1="78" x2="50" y2="75" stroke={colors.leaf} strokeWidth="1.5" strokeLinecap="round" />
    </g>
  );
}

function CherrySprout({ colors }) {
  return (
    <g>
      <ellipse cx="50" cy="88" rx="16" ry="4" fill="#5c3a1e" opacity="0.5" />
      <path d="M50 88 Q50 70 50 60" stroke={colors.stem} strokeWidth="3" fill="none" strokeLinecap="round" />
      <ellipse cx="42" cy="64" rx="7" ry="4" fill={colors.leaf} transform="rotate(-20 42 64)" />
      <ellipse cx="58" cy="64" rx="7" ry="4" fill={colors.leaf} transform="rotate(20 58 64)" opacity="0.85" />
      <ellipse cx="50" cy="57" rx="5" ry="7" fill={colors.leaf} />
    </g>
  );
}

function CherrySapling({ colors }) {
  return (
    <g>
      <ellipse cx="50" cy="90" rx="20" ry="4" fill="#3d2010" opacity="0.2" />
      {/* Support stake */}
      <line x1="55" y1="90" x2="55" y2="35" stroke="#8B7355" strokeWidth="2" strokeLinecap="round" />
      {/* Main stem */}
      <path d="M50 90 Q49 72 48 45" stroke={colors.stem} strokeWidth="4" fill="none" strokeLinecap="round" />
      {/* Branches */}
      <path d="M48 70 Q40 62 35 58" stroke={colors.stem} strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M49 58 Q58 50 63 46" stroke={colors.stem} strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* Leaves */}
      <ellipse cx="33" cy="55" rx="8" ry="4" fill={colors.leaf} transform="rotate(-15 33 55)" />
      <ellipse cx="64" cy="43" rx="8" ry="4" fill={colors.leaf} transform="rotate(15 64 43)" opacity="0.85" />
      <ellipse cx="44" cy="40" rx="7" ry="4" fill={colors.leaf} transform="rotate(-10 44 40)" />
      {/* Small green cherry tomato clusters */}
      <circle cx="36" cy="64" r="3" fill="#86efac" opacity="0.8" />
      <circle cx="39" cy="67" r="2.5" fill="#86efac" opacity="0.7" />
      <circle cx="61" cy="50" r="3" fill="#86efac" opacity="0.7" />
      <circle cx="64" cy="53" r="2.5" fill="#86efac" opacity="0.6" />
    </g>
  );
}

function CherryFull({ colors }) {
  return (
    <g>
      <ellipse cx="50" cy="92" rx="28" ry="5" fill="#3d2010" opacity="0.25" />
      {/* Support stake */}
      <line x1="56" y1="92" x2="56" y2="20" stroke="#8B7355" strokeWidth="2.5" strokeLinecap="round" />
      {/* Main stem */}
      <path d="M50 92 Q48 70 47 35" stroke={colors.stem} strokeWidth="5" fill="none" strokeLinecap="round" />
      {/* Branches */}
      <path d="M48 75 Q35 65 28 58" stroke={colors.stem} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M48 60 Q60 50 68 44" stroke={colors.stem} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M47 45 Q38 38 32 32" stroke={colors.stem} strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* Leaves */}
      <ellipse cx="26" cy="55" rx="9" ry="5" fill={colors.leaf} transform="rotate(-20 26 55)" />
      <ellipse cx="70" cy="40" rx="9" ry="5" fill={colors.leaf} transform="rotate(20 70 40)" opacity="0.85" />
      <ellipse cx="30" cy="30" rx="8" ry="4" fill={colors.leaf} transform="rotate(-15 30 30)" />
      <ellipse cx="55" cy="25" rx="7" ry="4" fill={colors.leaf} transform="rotate(10 55 25)" opacity="0.85" />
      {/* Cherry tomato clusters — small but numerous */}
      <circle cx="30" cy="62" r="4" fill={colors.fruit} />
      <circle cx="34" cy="66" r="3.5" fill={colors.fruit} opacity="0.9" />
      <circle cx="26" cy="66" r="3.5" fill={colors.fruit} opacity="0.85" />
      <circle cx="66" cy="47" r="4" fill={colors.fruit} />
      <circle cx="70" cy="50" r="3.5" fill={colors.fruit} opacity="0.9" />
      <circle cx="63" cy="51" r="3" fill={colors.fruit} opacity="0.85" />
      <circle cx="33" cy="35" r="3.5" fill={colors.fruit} opacity="0.9" />
      <circle cx="37" cy="38" r="3" fill={colors.fruit} opacity="0.85" />
      <circle cx="58" cy="55" r="3" fill={colors.fruit} opacity="0.8" />
      {/* Tomato highlights */}
      <circle cx="29" cy="60" r="1.5" fill="white" opacity="0.2" />
      <circle cx="65" cy="45" r="1.5" fill="white" opacity="0.2" />
      {/* Gentle sway animation */}
      <ellipse cx="50" cy="22" rx="7" ry="4" fill={colors.leaf} opacity="0.7">
        <animateTransform attributeName="transform" type="rotate" values="-2 50 22;2 50 22;-2 50 22" dur="4s" repeatCount="indefinite" />
      </ellipse>
    </g>
  );
}

// ============================================
// Main PlantSVG Component
// ============================================

export default function PlantSVG({
  species = 'roma',
  progress = 0,
  isWilting = false,
  isDead = false,
  size = 200,
  className = '',
}) {
  const plant = PLANT_SPECIES[species] || PLANT_SPECIES.roma;
  const stage = getGrowthStage(progress);

  // Determine visual styles
  const wiltFilter = isWilting ? 'saturate(0.4) brightness(0.7)' : 'none';
  const deadFilter = isDead ? 'saturate(0) brightness(0.5)' : 'none';
  const filter = isDead ? deadFilter : wiltFilter;
  const wiltTransform = isWilting && !isDead ? 'rotate(5deg)' : isDead ? 'rotate(15deg)' : 'none';

  return (
    <div
      className={`plant-svg-container ${className}`}
      style={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      role="img"
      aria-label={
        isDead
          ? `Wilted ${plant.name} plant`
          : isWilting
            ? `Wilting ${plant.name} plant - return to save it!`
            : `${plant.name} plant - ${Math.round(progress * 100)}% grown`
      }
    >
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        style={{
          filter,
          transform: wiltTransform,
          transition: 'filter 0.6s ease, transform 0.6s ease',
          willChange: 'transform, opacity',
        }}
      >
        {/* Ground */}
        <rect x="0" y="90" width="100" height="10" fill="#7c2d12" opacity="0.15" rx="5" />

        {/* Plant stage — crossfade between stages */}
        {plant.stages.map((Component, i) => (
          <g
            key={i}
            style={{
              opacity: i === stage ? 1 : 0,
              transform: i === stage ? 'scale(1)' : 'scale(0.8)',
              transition: 'opacity 1.2s ease, transform 1.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
              transformOrigin: '50px 88px',
            }}
          >
            {i === stage && <Component colors={plant.colors} />}
          </g>
        ))}
      </svg>
    </div>
  );
}

export { PLANT_SPECIES, getGrowthStage };
